import React, { useContext, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import Icon from './ui/Icon';
import { hasPermission } from '../utils/permissions';
import './Layout.css';

const APP_VERSION = "v2.0";

// Avatar com iniciais do usuário
const UserAvatar = ({ name, size = 36 }) => {
  const initials = useMemo(() => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0][0].toUpperCase();
  }, [name]);

  return (
    <div className="user-avatar" style={{ width: size, height: size, fontSize: size * 0.4 }}>
      {initials}
    </div>
  );
};

// Badge do tipo de usuário
const RoleBadge = ({ tipo }) => {
  const config = {
    admin: { label: 'Admin', className: 'role-admin' },
    encarregado: { label: 'Encarregado', className: 'role-encarregado' },
    secretario: { label: 'Secretário', className: 'role-secretario' },
    secretaria: { label: 'Secretário', className: 'role-secretario' },
    instrutor: { label: 'Instrutor', className: 'role-instrutor' },
  };
  const { label, className } = config[tipo] || config.instrutor;
  return <span className={`role-badge ${className}`}>{label}</span>;
};

const Layout = ({ children }) => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [showAlterarSenha, setShowAlterarSenha] = useState(false);
  const [alterarSenhaForm, setAlterarSenhaForm] = useState({ senhaAtual: '', novaSenha: '', confirmarSenha: '' });
  const [alterarSenhaError, setAlterarSenhaError] = useState('');
  const [alterarSenhaLoading, setAlterarSenhaLoading] = useState(false);
  const [alterarSenhaSuccess, setAlterarSenhaSuccess] = useState(false);
  const [pendAlertasPed, setPendAlertasPed] = useState(null);
  const [showFaq, setShowFaq] = useState(false);

  const isActive = (path) => location.pathname === path;
  const isAdminGroupActive = [
    '/usuarios',
    '/alertas',
    '/observabilidade',
    '/auditoria',
    '/backup',
    '/configuracoes/integracoes',
    '/configuracoes/aptidao',
    '/configuracoes/metas-pedagogicas',
    '/alertas-pedagogicos',
    '/admin'
  ].includes(location.pathname);

  useEffect(() => {
    if (isAdminGroupActive) {
      setAdminOpen(true);
    }
  }, [isAdminGroupActive]);

  // Fechar sidebar ao navegar no mobile e gerenciar scroll do body
  useEffect(() => {
    const handleNavigation = () => {
      if (window.innerWidth <= 991) {
        setSidebarOpen(false);
      }
    };
    handleNavigation();
  }, [location.pathname]);

  // Bloquear scroll do body quando menu mobile estiver aberto, evitando salto visual (scrollbar shift)
  useEffect(() => {
    if (sidebarOpen && window.innerWidth <= 991) {
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [sidebarOpen]);

  const openAlterarSenha = () => {
    setShowAlterarSenha(true);
    setAlterarSenhaForm({ senhaAtual: '', novaSenha: '', confirmarSenha: '' });
    setAlterarSenhaError('');
    setAlterarSenhaSuccess(false);
  };

  const closeAlterarSenha = () => {
    setShowAlterarSenha(false);
    setAlterarSenhaForm({ senhaAtual: '', novaSenha: '', confirmarSenha: '' });
    setAlterarSenhaError('');
    setAlterarSenhaSuccess(false);
  };

  const handleAlterarSenhaSubmit = async (e) => {
    e.preventDefault();
    setAlterarSenhaError('');
    if (alterarSenhaForm.novaSenha !== alterarSenhaForm.confirmarSenha) {
      setAlterarSenhaError('Nova senha e confirmação não conferem.');
      return;
    }
    if (alterarSenhaForm.novaSenha.length < 6) {
      setAlterarSenhaError('Nova senha deve ter no mínimo 6 caracteres.');
      return;
    }
    setAlterarSenhaLoading(true);
    try {
      await api.put('/usuarios/alterar-senha', {
        senhaAtual: alterarSenhaForm.senhaAtual,
        novaSenha: alterarSenhaForm.novaSenha,
        confirmarSenha: alterarSenhaForm.confirmarSenha
      });
      setAlterarSenhaSuccess(true);
      setTimeout(() => closeAlterarSenha(), 1500);
    } catch (err) {
      const msg = err.response?.data?.message || 'Não foi possível alterar a senha. Por favor, verifique os dados e tente novamente.';
      setAlterarSenhaError(msg);
    } finally {
      setAlterarSenhaLoading(false);
    }
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const closeSidebar = () => setSidebarOpen(false);
  const canManageUsers = hasPermission(user, 'usuarios.manage');
  const canManageDiasGem = hasPermission(user, 'configuracoes.dias_gem.manage');
  const canViewAlertasSistema = hasPermission(user, 'alertas.sistema.view');
  const canViewAlertasAptidao = hasPermission(user, 'alertas.aptidao.view');
  const canViewObservabilidade = hasPermission(user, 'observabilidade.view');
  const canViewAuditoria = hasPermission(user, 'auditoria.view');
  const canManageBackups = hasPermission(user, 'backup.manage');
  const canManageIntegracoes = hasPermission(user, 'configuracoes.integracoes.manage');
  const canManageAptidao = hasPermission(user, 'configuracoes.aptidao.manage');
  const canViewMetasPedagogicas = hasPermission(user, 'metas_pedagogicas.view');
  const canViewAdmin = hasPermission(user, 'admin.view');
  const canViewAlertasWhatsapp = hasPermission(user, 'alertas.whatsapp.view');
  const canViewFichas = hasPermission(user, 'fichas.view');
  const canViewInstrutores = hasPermission(user, 'instrutores.view');
  const canViewInstrutorPresencas = hasPermission(user, 'instrutores.presencas.view');
  const canViewInstrutorRelatorios = hasPermission(user, 'instrutores.relatorios.view');
  const canViewRelatoriosPedagogicos = hasPermission(user, 'relatorios.view');
  const canViewAcompanhamentos = hasPermission(user, 'acompanhamentos.view');
  const canViewAlertasPedagogicos = hasPermission(user, 'alertas_pedagogicos.view');
  const canManageInstrutorConfig = hasPermission(user, 'instrutores.configuracoes.manage');

  useEffect(() => {
    if (!canViewAlertasPedagogicos) {
      setPendAlertasPed(null);
      return undefined;
    }
    if (user?.tipo === 'admin' && !user?.gem_id) {
      setPendAlertasPed(null);
      return undefined;
    }
    let cancelled = false;
    api
      .get('/v1/alertas-pedagogicos/resumo')
      .then((r) => {
        if (!cancelled) setPendAlertasPed(Number(r.data?.resumo?.pendentes) || 0);
      })
      .catch(() => {
        if (!cancelled) setPendAlertasPed(null);
      });
    return () => {
      cancelled = true;
    };
  }, [canViewAlertasPedagogicos, user?.id, user?.tipo, user?.gem_id]);

  return (
    <div className="layout layout-ref">
      <header className="topbar-ref">
        <button
          type="button"
          className="mobile-menu-btn topbar-menu-btn"
          onClick={toggleSidebar}
          aria-label="Abrir menu"
        >
          <Icon name={sidebarOpen ? 'close' : 'menu'} size={20} color="#ffffff" />
        </button>
        <div className="topbar-title-ref">Sistema de Administração Musical</div>
        <div className="topbar-actions-ref">
          <button type="button" className="topbar-link-ref" onClick={() => setShowFaq(true)}>
            <Icon name="info" size={16} color="#ffffff" />
            <span>Dúvidas Frequentes</span>
          </button>
          <button type="button" className="topbar-user-ref" onClick={openAlterarSenha} title="Alterar senha">
            <Icon name="users" size={16} color="#ffffff" />
            <span>{user?.nome || 'Usuário'}</span>
          </button>
        </div>
      </header>

      <div className="layout-body-ref">
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        {/* Close button — only visible on mobile via CSS */}
        <button
          type="button"
          className="sidebar-mobile-close"
          onClick={closeSidebar}
          aria-label="Fechar menu"
        >
          <Icon name="close" size={18} color="currentColor" />
        </button>

        <div className="sidebar-user-ref">
          <UserAvatar name={user?.nome} size={48} />
          <div className="sidebar-user-email">{user?.email || user?.nome || 'Usuário'}</div>
          <div className="sidebar-online-ref">
            <span className="sidebar-online-dot" />
            Online
          </div>
          {user?.gem_nome && <div className="sidebar-gem-ref">{user.gem_nome}</div>}
        </div>

        <nav className="sidebar-nav" aria-label="Menu principal">
          <div className="nav-section-label">Principal</div>
          <Link to="/" className={`sidebar-link ${isActive('/') ? 'active' : ''}`} onClick={closeSidebar}>
            <Icon name="dashboard" size={20} />
            <span className="link-label">Painel de Controle</span>
          </Link>
          <Link to="/alunos" className={`sidebar-link ${isActive('/alunos') ? 'active' : ''}`} onClick={closeSidebar}>
            <Icon name="students" size={20} />
            <span className="link-label">Alunos</span>
          </Link>
          <Link
            to="/presencas"
            className={`sidebar-link ${isActive('/presencas') ? 'active' : ''}`}
            onClick={closeSidebar}
            title="Chamada rápida de presença. Lançamento completo da aula (progresso e contexto) no menu Alunos."
          >
            <Icon name="attendance" size={20} />
            <span className="link-label">Presença rápida</span>
          </Link>
          {canManageDiasGem && (
            <Link to="/dias-gem" className={`sidebar-link ${isActive('/dias-gem') ? 'active' : ''}`} onClick={closeSidebar}>
              <Icon name="calendar" size={20} />
              <span className="link-label">Dias do GEM</span>
            </Link>
          )}
          {hasPermission(user, 'configuracoes.dias_gem.manage') && (
            <Link to="/comuns" className={`sidebar-link ${isActive('/comuns') ? 'active' : ''}`} onClick={closeSidebar}>
              <Icon name="building" size={20} />
              <span className="link-label">Comum/Congregação</span>
            </Link>
          )}
          <Link to="/relatorios" className={`sidebar-link ${isActive('/relatorios') ? 'active' : ''}`} onClick={closeSidebar}>
            <Icon name="reports" size={20} />
            <span className="link-label">Relatórios</span>
          </Link>
          {canViewRelatoriosPedagogicos && (
            <Link
              to="/painel-pedagogico"
              className={`sidebar-link ${isActive('/painel-pedagogico') ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <Icon name="dashboard" size={20} />
              <span className="link-label">Painel pedagógico</span>
            </Link>
          )}
          {canViewAcompanhamentos && (
            <Link
              to="/acompanhamentos-pedagogicos"
              className={`sidebar-link ${isActive('/acompanhamentos-pedagogicos') ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <Icon name="clipboard" size={20} />
              <span className="link-label">Acompanhamentos</span>
            </Link>
          )}
          {canViewAlertasPedagogicos && (
            <Link
              to="/alertas-pedagogicos"
              className={`sidebar-link ${isActive('/alertas-pedagogicos') ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <Icon name="bell" size={20} />
              <span className="link-label">Alertas pedagógicos</span>
              {pendAlertasPed != null && pendAlertasPed > 0 && (
                <span className="sidebar-count-badge" title="Alertas pendentes">
                  {pendAlertasPed > 99 ? '99+' : pendAlertasPed}
                </span>
              )}
            </Link>
          )}
          {canViewFichas && (
            <Link to="/fichas" className={`sidebar-link ${isActive('/fichas') ? 'active' : ''}`} onClick={closeSidebar}>
              <Icon name="clipboard" size={20} />
              <span className="link-label">Fichas</span>
            </Link>
          )}
          {canViewAlertasWhatsapp && (
            <Link to="/alertas-whatsapp" className={`sidebar-link ${isActive('/alertas-whatsapp') ? 'active' : ''}`} onClick={closeSidebar}>
              <Icon name="whatsapp" size={20} />
              <span className="link-label">Alertas WhatsApp</span>
            </Link>
          )}

          {(canViewInstrutores || canViewInstrutorPresencas || canViewInstrutorRelatorios || canManageInstrutorConfig) && (
            <>
              <div className="nav-section-label">Gestão Musical</div>
              {canViewInstrutores && (
                <Link to="/instrutores" className={`sidebar-link ${isActive('/instrutores') ? 'active' : ''}`} onClick={closeSidebar}>
                  <Icon name="users" size={20} />
                  <span className="link-label">Instrutores</span>
                </Link>
              )}
              {canViewInstrutorPresencas && (
                <Link to="/instrutores/presencas" className={`sidebar-link ${isActive('/instrutores/presencas') ? 'active' : ''}`} onClick={closeSidebar}>
                  <Icon name="attendance" size={20} />
                  <span className="link-label">Presença Instrutores</span>
                </Link>
              )}
              {canViewInstrutorRelatorios && (
                <Link to="/instrutores/relatorios" className={`sidebar-link ${isActive('/instrutores/relatorios') ? 'active' : ''}`} onClick={closeSidebar}>
                  <Icon name="reports" size={20} />
                  <span className="link-label">Relatório Instrutores</span>
                </Link>
              )}
              {canManageInstrutorConfig && (
                <Link to="/instrutores/configuracoes" className={`sidebar-link ${isActive('/instrutores/configuracoes') ? 'active' : ''}`} onClick={closeSidebar}>
                  <Icon name="settings" size={20} />
                  <span className="link-label">Config. Instrutores</span>
                </Link>
              )}
            </>
          )}

          <div className="nav-section-label">Administração</div>
          <button
            type="button"
            className={`sidebar-group-toggle ${adminOpen ? 'open' : ''}`}
            onClick={() => setAdminOpen((prev) => !prev)}
            aria-expanded={adminOpen}
          >
            <Icon name="settings" size={20} />
            <span className="link-label">Configurações</span>
            <Icon name="chevronDown" size={16} className="chevron" />
          </button>
          <div className={`sidebar-group ${adminOpen ? 'open' : ''}`}>
            {canManageUsers && (
              <Link to="/usuarios" className={`sidebar-link ${isActive('/usuarios') ? 'active' : ''}`} onClick={closeSidebar}>
                <Icon name="users" size={20} />
                <span className="link-label">Usuários</span>
              </Link>
            )}
            {canViewAlertasSistema && (
              <Link to="/alertas" className={`sidebar-link ${isActive('/alertas') ? 'active' : ''}`} onClick={closeSidebar}>
                <Icon name="bell" size={20} />
                <span className="link-label">Alertas - Sistema</span>
              </Link>
            )}
            {canViewAlertasAptidao && (
              <Link to="/alertas-aptidao" className={`sidebar-link ${isActive('/alertas-aptidao') ? 'active' : ''}`} onClick={closeSidebar}>
                <Icon name="trumpet" size={20} />
                <span className="link-label">Alertas - Aptidão</span>
              </Link>
            )}
            {canViewObservabilidade && (
              <Link to="/observabilidade" className={`sidebar-link ${isActive('/observabilidade') ? 'active' : ''}`} onClick={closeSidebar}>
                <Icon name="info" size={20} />
                <span className="link-label">Observabilidade</span>
              </Link>
            )}
            {canViewAuditoria && (
              <Link to="/auditoria" className={`sidebar-link ${isActive('/auditoria') ? 'active' : ''}`} onClick={closeSidebar}>
                <Icon name="audit" size={20} />
                <span className="link-label">Auditoria</span>
              </Link>
            )}
            {canManageBackups && (
              <Link to="/backup" className={`sidebar-link ${isActive('/backup') ? 'active' : ''}`} onClick={closeSidebar}>
                <Icon name="backup" size={20} />
                <span className="link-label">Backup</span>
              </Link>
            )}
            {canManageIntegracoes && (
              <Link
                to="/configuracoes/integracoes"
                className={`sidebar-link ${isActive('/configuracoes/integracoes') ? 'active' : ''}`}
                onClick={closeSidebar}
              >
                <Icon name="integration" size={20} />
                <span className="link-label">Integrações</span>
              </Link>
            )}
            {canManageAptidao && (
              <Link
                to="/configuracoes/aptidao"
                className={`sidebar-link ${isActive('/configuracoes/aptidao') ? 'active' : ''}`}
                onClick={closeSidebar}
              >
                <Icon name="target" size={20} />
                <span className="link-label">Aptidão</span>
              </Link>
            )}
            {canViewMetasPedagogicas && (
              <Link
                to="/configuracoes/metas-pedagogicas"
                className={`sidebar-link ${isActive('/configuracoes/metas-pedagogicas') ? 'active' : ''}`}
                onClick={closeSidebar}
              >
                <Icon name="reports" size={20} />
                <span className="link-label">Metas pedagógicas</span>
              </Link>
            )}
            {canViewAdmin && (
              <Link to="/admin" className={`sidebar-link ${isActive('/admin') ? 'active' : ''}`} onClick={closeSidebar}>
                <Icon name="shield" size={20} />
                <span className="link-label">Admin</span>
              </Link>
            )}
          </div>
        </nav>

        <button type="button" className="sidebar-logout-ref" onClick={logout}>
          <Icon name="logout" size={18} />
          <span>Sair do Sistema</span>
        </button>
      </aside>

      <div className="main">
        <div className="content">{children}</div>
      </div>
      </div>

      {sidebarOpen && (
        <div
          className="overlay"
          onClick={closeSidebar}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') closeSidebar();
          }}
          aria-label="Fechar menu"
        />
      )}

      {showFaq && (
        <div className="modal" onClick={() => setShowFaq(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Dúvidas Frequentes</h2>
              <button type="button" className="modal-close-btn" onClick={() => setShowFaq(false)} aria-label="Fechar">
                <Icon name="close" size={20} />
              </button>
            </div>
            <p style={{ margin: 0, lineHeight: 1.6, color: '#374151' }}>
              Em caso de dúvidas sobre o uso do SisGEM, fale com o administrador do seu GEM.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn btn-primary" onClick={() => setShowFaq(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {showAlterarSenha && (
        <div className="modal" onClick={closeAlterarSenha}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Alterar senha</h2>
              <button className="modal-close-btn" onClick={closeAlterarSenha} aria-label="Fechar">
                <Icon name="close" size={20} />
              </button>
            </div>
            {alterarSenhaSuccess ? (
              <div className="alert alert-success">
                <Icon name="check" size={20} />
                Senha alterada com sucesso.
              </div>
            ) : (
              <form onSubmit={handleAlterarSenhaSubmit}>
                <div className="form-group">
                  <label htmlFor="senhaAtual">Senha atual</label>
                  <input
                    id="senhaAtual"
                    type="password"
                    value={alterarSenhaForm.senhaAtual}
                    onChange={(e) => setAlterarSenhaForm({ ...alterarSenhaForm, senhaAtual: e.target.value })}
                    placeholder="Digite sua senha atual"
                    required
                    autoComplete="current-password"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="novaSenha">Nova senha</label>
                  <input
                    id="novaSenha"
                    type="password"
                    value={alterarSenhaForm.novaSenha}
                    onChange={(e) => setAlterarSenhaForm({ ...alterarSenhaForm, novaSenha: e.target.value })}
                    placeholder="Mínimo 6 caracteres"
                    required
                    minLength={6}
                    autoComplete="new-password"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirmarSenha">Confirmar nova senha</label>
                  <input
                    id="confirmarSenha"
                    type="password"
                    value={alterarSenhaForm.confirmarSenha}
                    onChange={(e) => setAlterarSenhaForm({ ...alterarSenhaForm, confirmarSenha: e.target.value })}
                    placeholder="Repita a nova senha"
                    required
                    autoComplete="new-password"
                  />
                </div>
                {alterarSenhaError && (
                  <div className="alert alert-error">
                    <Icon name="warning" size={18} />
                    {alterarSenhaError}
                  </div>
                )}
                <div className="modal-actions">
                  <button type="button" onClick={closeAlterarSenha} className="btn btn-secondary">Cancelar</button>
                  <button type="submit" className="btn btn-primary" disabled={alterarSenhaLoading}>
                    {alterarSenhaLoading ? 'Alterando...' : 'Alterar senha'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired
};

export default Layout;
